import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { CACHE_TIMES } from '@/lib/queryClient'

/**
 * Prefetch critical data on app initialization
 * This reduces database load by caching commonly accessed data upfront
 */
export function useDataPrefetch() {
  const queryClient = useQueryClient()

  useEffect(() => {
    // Prefetch tours (static content)
    queryClient.prefetchQuery({
      queryKey: ['tours', 'all'],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('tours')
          .select(
            'id, slug, title, description, duration_days, level, price_from_usd, features, images, display_order'
          )
          .order('display_order', { ascending: true })

        if (error) throw error
        return data
      },
      ...CACHE_TIMES.STATIC,
    })

    // Prefetch featured tours (for home page)
    queryClient.prefetchQuery({
      queryKey: ['tours', 'featured'],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('tours')
          .select(
            'id, slug, title, description, duration_days, level, price_from_usd, features, images, display_order'
          )
          .order('display_order', { ascending: true })
          .limit(3)

        if (error) throw error
        return data
      },
      ...CACHE_TIMES.STATIC,
    })

    // Prefetch activities (static content) - excluding heavy columns
    queryClient.prefetchQuery({
      queryKey: ['activities', 'all'],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('activities')
          .select(
            'id, title, slug, description, activity_type, duration_minutes, price_usd, max_capacity, features, location, meeting_point, is_active, display_order, flight_options, images'
          )
          .eq('is_active', true)
          .order('display_order', { ascending: true })

        if (error) throw error
        return data
      },
      ...CACHE_TIMES.STATIC,
    })

    // Prefetch home page activities
    queryClient.prefetchQuery({
      queryKey: ['activities', 'home'],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('activities')
          .select(
            'id, title, slug, description, activity_type, duration_minutes, price_usd, max_capacity, features, location, meeting_point, is_active, display_order, flight_options, images'
          )
          .eq('is_active', true)
          .order('display_order', { ascending: true })

        if (error) throw error
        return data
      },
      ...CACHE_TIMES.STATIC,
    })

    // Prefetch hero backgrounds (site settings)
    queryClient.prefetchQuery({
      queryKey: ['hero-backgrounds'],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'hero_backgrounds')
          .single()

        if (error) return null
        return data?.value
      },
      ...CACHE_TIMES.STATIC,
    })

    // Prefetch blog posts list (semi-static)
    queryClient.prefetchQuery({
      queryKey: ['blog', 'list'],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('blog_posts')
          .select(
            `
            id,
            slug,
            status,
            published_at,
            read_time_minutes,
            featured_image_id,
            category:blog_categories(id, slug, translation:blog_category_translations!inner(name, language_code)),
            content:blog_content!inner(title, excerpt, language_code),
            tags:blog_post_tags(tag:blog_tags(id, translation:blog_tag_translations!inner(name, language_code)))
          `
          )
          .eq('status', 'published')
          .eq('content.language_code', 'en')
          .order('published_at', { ascending: false })
          .limit(10)

        if (error) return []
        return data
      },
      ...CACHE_TIMES.SEMI_STATIC,
    })

    // Prefetch guide count (static)
    queryClient.prefetchQuery({
      queryKey: ['guides', 'count'],
      queryFn: async () => {
        const { count, error } = await supabase
          .from('guides')
          .select('*', { count: 'exact', head: true })

        if (error) return 0
        return count || 0
      },
      ...CACHE_TIMES.STATIC,
    })
  }, [queryClient])
}

/**
 * Prefetch a single tour by slug (for link hover prefetching)
 */
export function usePrefetchTour() {
  const queryClient = useQueryClient()

  return (slug: string) => {
    queryClient.prefetchQuery({
      queryKey: ['tour', slug],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('tours')
          .select('*')
          .eq('slug', slug)
          .single()

        if (error) throw error
        return data
      },
      ...CACHE_TIMES.STATIC,
    })
  }
}

/**
 * Prefetch a single activity by slug
 */
export function usePrefetchActivity() {
  const queryClient = useQueryClient()

  return (slug: string) => {
    queryClient.prefetchQuery({
      queryKey: ['activity', slug],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('activities')
          .select('*')
          .eq('slug', slug)
          .single()

        if (error) throw error
        return data
      },
      ...CACHE_TIMES.STATIC,
    })
  }
}

/**
 * Prefetch a blog post by slug
 */
export function usePrefetchBlogPost() {
  const queryClient = useQueryClient()

  return (slug: string) => {
    queryClient.prefetchQuery({
      queryKey: ['blog', 'post', slug],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('blog_published_posts')
          .select('*')
          .eq('slug', slug)
          .single()

        if (error) throw error
        return data
      },
      ...CACHE_TIMES.SEMI_STATIC,
    })
  }
}
